/**
 * Routes.
 *
 * Three screens, flat. The list keeps its filters in the query string so a filtered view
 * can be pasted into a chat and reopened identically — that is a requirement, not a
 * nicety, and it is why the list reads its filters from `route.query` rather than from
 * component state.
 *
 * There is deliberately **no edit route**. A preview is a live thing and the detail view
 * edits it in place — its tags on the service rows, its chart version and description
 * where they are read, its variables inside each service. Sending someone to a separate
 * page to change one field was the odd one out in an interface that already edited the
 * expiry and the variables inline, and the form's habit of sending every field made
 * changing a tag reset the lifetime.
 */

import { createRouter, createWebHistory } from 'vue-router'

/** Exported so a test can build the same table over a memory history. */
export const routes = [
  {
    path: '/',
    name: 'previews',
    component: () => import('@/views/PreviewListView.vue'),
    meta: { title: 'Previews' },
  },
  {
    path: '/new',
    name: 'preview-create',
    component: () => import('@/views/PreviewCreateView.vue'),
    meta: { title: 'Create a preview' },
  },
  {
    path: '/p/:name',
    name: 'preview-detail',
    component: () => import('@/views/PreviewDetailView.vue'),
    props: true,
    meta: { title: 'Preview' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page not found' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (to, from, saved) => saved || { top: 0 },
})

router.afterEach((to) => {
  const title = to.name === 'preview-detail' ? to.params.name : to.meta.title
  document.title = title ? `${title} — PreviewApp` : 'PreviewApp'
})

export default router
